import styled from '@emotion/styled';
import { useMemo, useRef } from 'react';
import { useDragLayer } from 'react-dnd';

import { Glyph } from '../common/Glyph';

import { useIsRTL } from '../../hooks/useIsRTL';

import { su } from '../../theme/functions/su';
import { theme } from '../../theme/theme';

import { dragTypes } from './dragTypes';

function getItemStyles(initialOffset, currentOffset, isRTL, itemRef, item, itemType) {
  if (!initialOffset || !currentOffset) {
    return {
      display: 'none',
    };
  }

  let { x, y } = currentOffset;

  if (itemType === dragTypes.ZONE && isRTL && itemRef.current) {
    x = currentOffset.x + item.originalWidth - itemRef.current.offsetWidth;
  }

  const transform = `translate(${x}px, ${y}px)`;
  return {
    transform,
    WebkitTransform: transform,
  };
}

export function DragLayer(props) {
  const isRTL = useIsRTL();
  const itemRef = useRef(null);

  const { itemType, isDragging, item, initialOffset, currentOffset } = useDragLayer((monitor) => {
    return {
      item: monitor.getItem(),
      itemType: monitor.getItemType(),
      initialOffset: monitor.getInitialSourceClientOffset(),
      currentOffset: monitor.getSourceClientOffset(),
      isDragging: monitor.isDragging(),
    };
  });

  const rendered = useMemo(() => {
    if (item && item.render) {
      return item.render();
    }
  }, [item]);

  if (!isDragging) {
    return null;
  }

  const renderedItem = renderItem({ itemType, item, rendered });

  return (
    renderedItem != null && (
      <DragLayer.Root>
        <DragLayer.Item
          ref={itemRef}
          style={getItemStyles(initialOffset, currentOffset, isRTL, itemRef, item, itemType)}
        >
          {renderedItem}
        </DragLayer.Item>
      </DragLayer.Root>
    )
  );
}

function renderItem({ itemType, item, rendered }) {
  switch (itemType) {
    case dragTypes.ZONE:
      return <ZonePreview item={item} />;
    case dragTypes.FLOW:
    case dragTypes.FLOW_CARD_ACTION:
    case dragTypes.FLOW_CARD_CONDITION:
    case dragTypes.DEVICE:
    case dragTypes.MOOD:
    case dragTypes.LOG:
    case dragTypes.FOLDER:
    case dragTypes.ITEM:
      return rendered;
    default:
      return null;
  }
}

function ZonePreview({ item }) {
  return (
    <ZonePreviewContainer style={{ '--level-margin': `${item.level * 20}px` }}>
      <ZonePreviewContent>
        <Glyph source={item.iconUrl} size={theme.icon.size_default} color={theme.color.icon_dark} />
        <ZonePreviewName>{item.name}</ZonePreviewName>
      </ZonePreviewContent>
    </ZonePreviewContainer>
  );
}

DragLayer.Root = styled.div`
  position: fixed;
  z-index: ${theme.zIndex.drag_layer};
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  direction: ltr;
`;

DragLayer.Item = styled.div`
  width: fit-content;
`;

const ZonePreviewContainer = styled.div`
  display: inline-block;
  min-width: 160px;
  max-width: 256px;
  height: ${su(5)};
  border-radius: ${theme.borderRadius.default};
  background-color: ${theme.color.white};
  box-shadow: ${theme.boxShadow.default};
  isolation: isolate;
  margin-inline-start: var(--level-margin);
  direction: ltr;

  &:lang(ar) {
    direction: rtl;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-color: ${theme.color.component};
    opacity: 0.9;
    border-radius: ${theme.borderRadius.default};
    z-index: -1;
    margin-inline-start: var(--level-margin);
  }
`;

const ZonePreviewContent = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  padding: ${su(1, 2)};
`;

const ZonePreviewName = styled.div`
  margin-inline-start: ${su(1)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
